import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import { higherTimeframes, sanitizeHtfTimeframes, mtfLabel, MTF_MAX_ADDITIONAL } from '@/utils/mtf'
import { TIMEFRAMES } from '@/constants/timeframes'
import { defaultConfig } from './index'

function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ ...config, panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

const STYLE_KEYS = [
  'colorBull', 'colorBear',
  'fillOpacity', 'ifvgFillOpacity', 'borderWidth',
]

const LOGIC_KEYS = [
  'gapFilter', 'fillMode', 'mitigationPct', 'ifvgLookback',
  'minGapSizePercent', 'minIfvgSizePercent', 'boxLengthBars',
]

export default function FvgIfvgQuantumPanel({ config, onChange, onClose, chartTimeframe }) {
  const cfg = config || {}

  const update = (key, value) => onChange({ ...cfg, [key]: value })

  // MTF: solo se ofrecen TFs estrictamente mayores que el del chart; la
  // selección se sanea contra el chart actual (un TF que deja de ser mayor
  // desaparece de los chips y deja de pedirse, sin borrar la config).
  const htfOptions = higherTimeframes(chartTimeframe)
  const htfSelected = sanitizeHtfTimeframes(cfg.htfTimeframes ?? [], chartTimeframe)
  const chartTfLabel = TIMEFRAMES.find((t) => t.value === chartTimeframe)?.label ?? chartTimeframe
  const toggleHtf = (tf) => {
    if (htfSelected.includes(tf)) {
      update('htfTimeframes', htfSelected.filter((t) => t !== tf))
    } else if (htfSelected.length < MTF_MAX_ADDITIONAL) {
      update('htfTimeframes', [...htfSelected, tf])
    }
  }

  const resetStyle = () => {
    const patch = {}
    for (const k of STYLE_KEYS) patch[k] = defaultConfig[k]
    onChange({ ...cfg, ...patch })
  }

  return (
    <IndicatorConfigModal title="Configuración FVG / IFVG Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">

        <Section title="Zonas" sectionKey="zones" config={cfg} onChange={onChange}>
          <ToggleField label="FVG alcistas (BISI)" value={cfg.showBullFVG ?? true} onChange={(v) => update('showBullFVG', v)} />
          <ToggleField label="FVG bajistas (SIBI)" value={cfg.showBearFVG ?? true} onChange={(v) => update('showBearFVG', v)} />
          <ToggleField label="Inversiones FVG (IFVG)" value={cfg.showIFVG ?? true} onChange={(v) => update('showIFVG', v)} />
          <ToggleField label="Etiquetas" value={cfg.showLabels ?? true} onChange={(v) => update('showLabels', v)} />
          <SliderField label="Máx. zonas visibles" value={cfg.maxVisibleZones ?? 10} min={0} max={50} step={1} onChange={(v) => update('maxVisibleZones', v)} />
        </Section>

        <Section title="Lógica" sectionKey="logic" config={cfg} onChange={onChange}>
          <div className="space-y-1">
            <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Modo mitigación</span>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'Close', label: 'Close' },
                { value: 'Wick', label: 'Wick' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => update('fillMode', opt.value)}
                  className={`px-2 py-1.5 text-[11px] font-medium rounded-lg border transition-colors ${
                    (cfg.fillMode ?? 'Close') === opt.value
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                      : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <SliderField label="Mitigación %" value={cfg.mitigationPct ?? 50} min={0} max={100} step={1} unit="%" onChange={(v) => update('mitigationPct', v)} />
          <SliderField label="Filtro de gap" value={cfg.gapFilter ?? 0.5} min={0} max={3} step={0.1} unit="σ" onChange={(v) => update('gapFilter', v)} />
          <SliderField label="Lookback IFVG" value={cfg.ifvgLookback ?? 5} min={0} max={50} step={1} unit=" velas" onChange={(v) => update('ifvgLookback', v)} />
          <div className="space-y-1" data-testid="fvg-mtf-section">
            <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Timeframes adicionales</span>
            {htfOptions.length === 0 ? (
              <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
                No hay timeframes superiores disponibles desde {chartTfLabel}.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-1">
                  {htfOptions.map((tf) => {
                    const selected = htfSelected.includes(tf)
                    const disabled = !selected && htfSelected.length >= MTF_MAX_ADDITIONAL
                    return (
                      <button
                        key={tf}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggleHtf(tf)}
                        aria-pressed={selected}
                        data-tf={tf}
                        className={`px-1 py-1 text-[10px] font-medium rounded border transition-colors ${
                          selected
                            ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                            : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-white'
                        }`}
                      >
                        {mtfLabel(tf)}
                      </button>
                    )
                  })}
                </div>
                <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
                  FVG/IFVG de timeframes mayores: borde punteado y etiqueta con sufijo (p. ej. BISI · 4H). Su ciclo de vida se evalúa en su propio timeframe. Máx. {MTF_MAX_ADDITIONAL}.
                </p>
              </>
            )}
          </div>
        </Section>

        <Section title="Tamaño mínimo del gap" sectionKey="size" config={cfg} onChange={onChange}>
          <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
            Gaps más pequeños que el umbral (% del precio medio de la zona) no se pintan ni etiquetan.
          </p>
          <SliderField label="FVG" value={cfg.minGapSizePercent ?? 0} min={0} max={2} step={0.05} unit="%" onChange={(v) => update('minGapSizePercent', v)} />
          <SliderField label="IFVG" value={cfg.minIfvgSizePercent ?? 0} min={0} max={2} step={0.05} unit="%" onChange={(v) => update('minIfvgSizePercent', v)} />
          <SliderField label="Longitud de caja" value={cfg.boxLengthBars ?? 50} min={10} max={200} step={1} unit=" barras" onChange={(v) => update('boxLengthBars', v)} />
          <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
            Extensión máxima a la derecha para FVG e IFVG. Una sola regla compartida para ambos tipos.
          </p>
        </Section>

        <Section title="Colores" sectionKey="colors" config={cfg} onChange={onChange}>
          <ColorPickerField label="BISI / FVG alcista" value={cfg.colorBull ?? '#1B5E20'} onChange={(v) => update('colorBull', v)} />
          <ColorPickerField label="SIBI / FVG bajista" value={cfg.colorBear ?? '#B71C1C'} onChange={(v) => update('colorBear', v)} />
          <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
            Los IFVG usan la misma familia de color que su dirección (alcista = BISI, bajista = SIBI) con borde discontinuo.
          </p>
        </Section>

        <Section title="Estilo avanzado" sectionKey="style" config={cfg} onChange={onChange}>
          <SliderField label="Opacidad FVG" value={cfg.fillOpacity ?? 0.10} min={0} max={0.5} step={0.01} onChange={(v) => update('fillOpacity', v)} />
          <SliderField label="Opacidad IFVG" value={cfg.ifvgFillOpacity ?? 0.06} min={0} max={0.5} step={0.01} onChange={(v) => update('ifvgFillOpacity', v)} />
          <SliderField label="Grosor de borde" value={cfg.borderWidth ?? 1} min={0} max={3} step={0.5} unit="px" onChange={(v) => update('borderWidth', v)} />
          <button
            onClick={resetStyle}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
          >
            <RotateCcw size={12} />
            Restablecer estilo Quantum
          </button>
        </Section>

      </div>
    </IndicatorConfigModal>
  )
}
